import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { useColorScheme } from "../../hooks/useColorScheme";
import { useOutsideAlerter } from "../../hooks/useOutsideAlerter";
import { authSelectors } from "../../store/auth/selector";
import { actions } from "../../store/auth/slice";
import {
  AccountFragmentFragment,
  AccountQuery,
  DealStatus,
  FirmAccountType,
} from "../../graphql/generated";

import {
  ArrowRightIcon,
  Bars3Icon,
  UserCircleIcon,
  Cog6ToothIcon,
  ArrowLeftOnRectangleIcon,
} from "@heroicons/react/24/solid";
import Logo from "../Logo";
import LogoIcon from "../LogoIcon";
import { getNavLogoConfig } from "../../branding/themeAssets";
import { useQueryClient } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import { useAuth, useClerk, useSession } from "@clerk/clerk-react";
import { Avatar } from "../account/Avatar";
import { classNames } from "../../utils/cn";
import { formatDistanceToNowStrict, fromUnixTime } from "date-fns";
import React, { Fragment } from "react";
import {
  BellIcon,
  BriefcaseIcon,
  GitBranchIcon,
  LifeBuoyIcon,
  PanelLeftIcon,
  PlusIcon,
  TagIcon,
} from "lucide-react";
import { SignupSource } from "../../routes/Signup";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { AppState } from "@/src/store";
import { WithMarginProp } from "@/src/styles/withMargin";
import { NotificationDot } from "./NotificationDot";
import { NotificationPanel } from "./NotifcationPanel";
import { SlideOver } from "../SlideOver";
import clsx from "clsx";
import CompanyLogo from "../CompanyLogo";
import { CreateDealModal } from "./CreateDealModal";
import { useInvalidateQueryKeys } from "@/src/hooks/useInvalidateQueryKeys";
import { FirmRoleRestricted } from "../FirmRoleRestricted";

interface MobileNavProps {
  menuToggle: () => void;
  showMenu: boolean;
}

const MobileHeader = (props: MobileNavProps) => {
  const account = useSelector(authSelectors.account);
  return (
    <header className="block md:hidden bg-persian-950 py-3 px-2 shadow-xl">
      <div className="flex justify-between items-center">
        <div className="flex gap-x-3 items-center shadow-xl">
          <Bars3Icon
            onClick={props.menuToggle}
            className="text-white w-6 h-6"
          />
          <Logo width={74} height={25} fill="#fff" />
        </div>
        {account && account.firm ? (
          <Popover className="relative z-30">
            {({ open }) => (
              <>
                <Popover.Button className="relative p-2 border border-persian-50/30 hover:border-persian-50/50 rounded-md bg-persian-950/80 hover:bg-persian-950/90">
                  <BellIcon className="w-4 h-4 text-white" />
                  <NotificationDot />
                </Popover.Button>
                {open && <NotificationPanel isMobile={true} />}
              </>
            )}
          </Popover>
        ) : null}
      </div>
    </header>
  );
};

export function Navigation() {
  const [showMenu, setShowMenu] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const location = useLocation();
  const colorScheme = useColorScheme();
  const navLogo = getNavLogoConfig();

  const { signOut } = useAuth();
  const { session } = useSession();

  const dealsLastSeen = useSelector(authSelectors.dealsLastSeen);
  const isCollapsed = useSelector(authSelectors.navigationCollapsed);

  const account = useSelector(authSelectors.account);
  const wrapperRef = useRef(null);
  const queryClient = useQueryClient();

  const [openModal, setOpenModal] = useState<"account" | "create_deal" | "">(
    "",
  );

  const dispatch = useDispatch();
  const history = useHistory();

  const posthog = usePostHog();

  const sortedDeals = React.useMemo(() => {
    if (!account || !account.deals) return [];

    return account.deals
      .filter((deal) => deal.status === DealStatus.Active)
      .sort((a, b) => {
        const aLastSeen = dealsLastSeen[a.id] || a.createdAt;
        const bLastSeen = dealsLastSeen[b.id] || b.createdAt;
        return bLastSeen - aLastSeen;
      });
  }, [account, dealsLastSeen]);

  const [dealsSearch, setDealsSearch] = useState("");

  useOutsideAlerter(wrapperRef, () => {
    setShowDropdown(false);
  });

  useEffect(() => {
    setShowMenu(false);
  }, [location.pathname]);

  const signupSource =
    (session?.user.unsafeMetadata?.signupSource as SignupSource) ??
    SignupSource.signupPage;

  const { openUserProfile } = useClerk();

  return (
    <>
      <MobileHeader
        showMenu={showMenu}
        menuToggle={() => setShowMenu((s) => !s)}
      />
      <Nav
        colorScheme={colorScheme}
        showMenu={showMenu}
        setShowMenu={setShowMenu}
        isCollapsed={isCollapsed}
        isHovered={isHovered}
        setIsHovered={setIsHovered}
      >
        <div
          className={clsx(
            "flex min-h-0 flex-1 mt-3 flex-col transition-all duration-300",
            isCollapsed ? "mx-0" : "mx-1.5",
          )}
        >
          {/* Header - Fixed at top */}
          <div
            className={clsx(
              "flex-shrink-0 mb-5 flex items-center",
              isCollapsed ? "mx-2 justify-center" : "ml-4 mr-2 justify-between",
            )}
          >
            <div
              className={clsx(
                "transition-all duration-300 ease-out",
                isCollapsed ? "w-8" : navLogo.containerClass,
              )}
            >
              {isCollapsed ? (
                isHovered ? (
                  <button
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-persian-900/60 group-hover:bg-persian-900/80 transition-all duration-200 cursor-e-resize"
                    onClick={() =>
                      dispatch(
                        actions.setNavigationCollapsed({ collapsed: false }),
                      )
                    }
                  >
                    <PanelLeftIcon className="w-5 h-5 text-persian-400" />
                  </button>
                ) : (
                  <div className="w-8 h-8 flex items-center justify-center">
                    <LogoIcon className="w-6 h-6" />
                  </div>
                )
              ) : (
                <Logo width={navLogo.width} height={navLogo.height} />
              )}
            </div>

            {!isCollapsed && (
              <button
                className="group-hover:bg-persian-900/80 rounded-lg p-1 cursor-w-resize transition-all duration-200 hover:bg-persian-900/60"
                onClick={() =>
                  dispatch(actions.setNavigationCollapsed({ collapsed: true }))
                }
              >
                <PanelLeftIcon className="w-5 h-5 text-persian-400" />
              </button>
            )}
          </div>

          {/* Scrollable content area */}
          <div className="flex-1 flex flex-col min-h-0">
            <FirmNavigation
              account={account}
              signupSource={signupSource}
              isCollapsed={isCollapsed}
            />
            <div className="mx-2 h-px w-auto bg-persian-600"></div>

            {account && account.deals && account.deals.length > 0 ? (
              <div className="min-h-0 py-2 flex-1 overflow-y-auto px-1 no-scrollbar space-y-1">
                {account && account.deals
                  ? sortedDeals
                      .filter((deal) => {
                        return dealsSearch.length > 2
                          ? deal.company.name
                              .toLowerCase()
                              .includes(dealsSearch.toLowerCase())
                          : true;
                      })
                      .map((deal) => {
                        return (
                          <DealListItem
                            key={deal.id}
                            deal={deal}
                            isCollapsed={isCollapsed}
                          />
                        );
                      })
                  : null}

                <FirmRoleRestricted roles={[FirmAccountType.Admin]}>
                  <button
                    onClick={() => {
                      setOpenModal("create_deal");
                    }}
                    className={clsx(
                      "pt-2 group text-white  hover:text-persian-300 flex items-center font-bold text-sm pb-2",
                      isCollapsed
                        ? "px-2 justify-center w-full "
                        : "gap-x-3 mx-2",
                    )}
                  >
                    <div className="p-2 bg-[var(--create-deal-btn)] group-hover:bg-persian-900 rounded-full">
                      <PlusIcon className="w-3 h-3 text-persian-300" />
                    </div>
                    <span
                      className={clsx(
                        isCollapsed
                          ? "opacity-0 max-w-0 overflow-hidden"
                          : "opacity-100 w-full",
                      )}
                    >
                      Create new deal
                    </span>
                  </button>
                </FirmRoleRestricted>
              </div>
            ) : (
              <div
                className={clsx(
                  "flex-1 p-3 bg-persian-950 border-persian-50/30 mx-2 border rounded-md transition-all duration-300",
                  isCollapsed ? "px-2" : "px-3",
                )}
              >
                {!isCollapsed && (
                  <>
                    <p className="text-white text-sm font-semibold">No deals</p>
                    <p className="text-gray-200 text-sm">
                      All your deals will appear here
                    </p>

                    {account && account.firm ? (
                      <button
                        className={
                          "text-white font-semibold text-sm  mt-3 group flex items-center"
                        }
                        onClick={() => {
                          setOpenModal("create_deal");
                        }}
                      >
                        Create my first deal
                        <ArrowRightIcon className="text-white w-4 h-4 group-hover:ml-2 ml-0.5 transition-all" />
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer - Fixed at bottom */}
          <div
            className={clsx(
              "relative z-40 flex-shrink-0 sticky bottom-0 left-0 overflow-visible",
              account && account.deals.length > 5
                ? "border-t border-persian-800 pt-2"
                : "",
            )}
          >
            <div className="mx-3">
              {account ? (
                <Popover className="relative z-30 w-full">
                  {({ open }) => (
                    <>
                      <PopoverButton
                        className={clsx(
                          "flex items-center rounded-md px-2 py-1.5 w-full",
                          "focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-0 focus:shadow-none",
                          "active:outline-none active:ring-0 active:border-0 active:shadow-none",
                          "transition-none select-none",
                          isCollapsed ? "px-2 justify-center" : "gap-x-3",
                          open
                            ? "bg-white text-persian-990"
                            : " text-white  hover:bg-persian-900",
                        )}
                      >
                        <div className="relative">
                          <BellIcon
                            className={clsx(
                              "w-6 h-6 ",
                              open ? "text-persian-900" : "text-persian-300",
                            )}
                          />
                          {isCollapsed ? (
                            <NotificationDot className="absolute -top-1.5 -right-3" />
                          ) : null}
                        </div>

                        <div
                          className={clsx(
                            "flex items-center justify-between",
                            isCollapsed
                              ? "opacity-0 max-w-0 overflow-hidden"
                              : "opacity-100 w-full",
                          )}
                        >
                          <p
                            className={clsx(
                              "text-sm font-semibold transition-all duration-300 whitespace-nowrap",
                            )}
                          >
                            Notifications
                          </p>
                          {!isCollapsed ? <NotificationDot /> : null}
                        </div>
                      </PopoverButton>
                      {open && <NotificationPanel isCollapsed={isCollapsed} />}
                    </>
                  )}
                </Popover>
              ) : null}
            </div>

            <div>
              <div className="mx-3">
                <Popover className="relative z-30 w-full">
                  {({ open, close }) => (
                    <>
                      <PopoverButton
                        className={clsx(
                          "py-1.5 my-1.5 px-2  font-semibold rounded-md flex items-center w-full text-left border-0 bg-transparent",
                          "focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-0 focus:shadow-none",
                          "active:outline-none active:ring-0 active:border-0 active:shadow-none",
                          "transition-none select-none",
                          isCollapsed ? "px-2 justify-center" : "gap-x-3",
                          open
                            ? "bg-white text-persian-990"
                            : " text-white  hover:bg-persian-900",
                        )}
                      >
                        <div
                          className={clsx(
                            "flex items-center",
                            isCollapsed ? "" : "gap-x-3",
                          )}
                        >
                          {account ? (
                            <Avatar
                              size="m"
                              account={account}
                              withBorder={false}
                            />
                          ) : (
                            <UserCircleIcon className="w-4 h-4 " />
                          )}
                          <span
                            className={clsx(
                              " whitespace-nowrap",
                              isCollapsed
                                ? "opacity-0 max-w-0 overflow-hidden"
                                : "opacity-100 max-w-full",
                            )}
                          >
                            {account ? (
                              <>
                                <p className="font-bold  text-sm leading-tight">
                                  {account.name}
                                </p>
                                <p className="text-xs font-normal">
                                  {account.email}
                                </p>
                              </>
                            ) : null}
                          </span>
                        </div>
                      </PopoverButton>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-2"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-2"
                      >
                        <PopoverPanel
                          className={clsx(
                            "absolute bottom-full mb-2 z-50 w-64 rounded-lg bg-white shadow-lg ",
                            isCollapsed ? "left-0" : "left-3",
                          )}
                        >
                          <div className="p-1">
                            <button
                              onClick={() => {
                                close();
                                openUserProfile();
                              }}
                              className="w-full text-left px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-x-3 transition-colors"
                            >
                              <Cog6ToothIcon className="w-4 h-4 text-gray-500 " />
                              Account Settings
                            </button>

                            <button
                              onClick={() => {
                                close();
                                (window as any).Plain?.open();
                              }}
                              className="w-full text-left px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-x-3 transition-colors"
                            >
                              <LifeBuoyIcon className="w-4 h-4 text-gray-500" />
                              Help & Support
                            </button>

                            <div className="border-t border-gray-100 my-1" />

                            <button
                              onClick={async () => {
                                await signOut();
                                posthog?.reset();

                                dispatch(actions.logout());
                                queryClient.removeQueries();
                                queryClient.invalidateQueries();
                                queryClient.invalidateQueries({
                                  queryKey: ["Account", {}],
                                });
                                history.push("/login");
                              }}
                              className="w-full text-left px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-md flex items-center gap-x-3 transition-colors"
                            >
                              <ArrowLeftOnRectangleIcon className="w-4 h-4 text-red-500" />
                              Logout
                            </button>
                          </div>
                        </PopoverPanel>
                      </Transition>
                    </>
                  )}
                </Popover>
              </div>
            </div>
          </div>
        </div>
        <CreateDealModal
          open={openModal === "create_deal"}
          onClose={() => {
            setOpenModal("");
          }}
        />
      </Nav>
    </>
  );
}

function FirmNavigation(props: {
  account: AccountFragmentFragment | undefined;
  signupSource: SignupSource;
  isCollapsed: boolean;
}) {
  const dispatch = useDispatch();
  const location = useLocation();

  if (!props.account) {
    return null;
  }

  if (props.account.firm) {
    return (
      <div className="mb-2">
        <Link margin="xl 0 0 0" to="/deals" isCollapsed={props.isCollapsed}>
          <div
            className={clsx(
              "flex items-center",
              props.isCollapsed ? "" : "space-x-3",
            )}
          >
            <TagIcon
              className={clsx(
                "w-6 h-6 ",
                location.pathname.startsWith("/deals")
                  ? "text-persian-800"
                  : "text-persian-300",
              )}
            />
            <span
              className={clsx(
                "text-sm font-semibold transition-all duration-300 whitespace-nowrap",
                props.isCollapsed
                  ? "opacity-0 max-w-0 overflow-hidden"
                  : "opacity-100 max-w-full",
              )}
            >
              Deals
            </span>
          </div>
        </Link>

        <Link size="s" to="/firm" isCollapsed={props.isCollapsed}>
          <div
            className={clsx(
              "flex items-center",
              props.isCollapsed ? "" : "gap-x-3",
            )}
          >
            <BriefcaseIcon
              className={clsx(
                "w-6 h-6 ",
                location.pathname.startsWith("/firm")
                  ? "text-persian-800"
                  : "text-persian-300",
              )}
            />
            <span
              className={clsx(
                "text-sm transition-all duration-300 whitespace-nowrap",
                props.isCollapsed
                  ? "opacity-0 max-w-0 overflow-hidden"
                  : "opacity-100 max-w-full",
              )}
            >
              {props.account.firm ? props.account.firm.name : "Firm"}
            </span>
          </div>
        </Link>
      </div>
    );
  }

  // if (props.signupSource === SignupSource.externalInvite) {
  //   return (
  //     <div
  //       className={clsx(
  //         "p-3 rounded-md bg-persian-950/90 mt-3 border border-persian-50/30 transition-all duration-300",
  //         props.isCollapsed ? "px-2" : "px-3",
  //       )}
  //     >
  //       {!props.isCollapsed && (
  //         <>
  //           <p className="text-md text-gray-50 font-semibold">
  //             Sign up to Liquid
  //           </p>
  //           <p className="text-sm text-gray-50">
  //             Find out why more and more firms are switching to Liquid to run
  //             their deals
  //           </p>
  //           <a
  //             href={`mailto:hello@liquidacquire.com?subject=${encodeURIComponent(
  //               "I'm interested in Liquid",
  //             )}`}
  //             target="_blank"
  //             rel="noreferrer"
  //           >
  //             <p className="text-white font-semibold text-sm  mt-3 group flex items-center">
  //               Talk to us
  //               <ArrowRightIcon className="text-white w-4 h-4 group-hover:ml-2 ml-0.5 transition-all" />
  //             </p>
  //           </a>
  //         </>
  //       )}
  //     </div>
  //   );
  // }

  return <div></div>;
}

function DealListItem(props: {
  deal: AccountQuery["account"]["deals"][0];
  isCollapsed: boolean;
}) {
  const deal = props.deal;
  const history = useHistory();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { invalidatePermissionChecks } = useInvalidateQueryKeys();

  // Instead of using useParams which might not work in this context,
  // check if the current path includes this deal's ID
  const currentPath = location.pathname;
  const isActive = currentPath.includes(`/deal/${deal.id}`);

  const dealLastSeen = useSelector((state: AppState) =>
    authSelectors.dealLastSeen(state, deal.id),
  );

  const dispatch = useDispatch();
  const lastSeen = dealLastSeen ? dealLastSeen : deal.createdAt;

  return (
    <div
      className={clsx(
        "group py-2 text-secondary-text rounded-md cursor-pointer hover:bg-persian-900 hover:text-white transition-all duration-200",
        props.isCollapsed ? "px-2 mx-1 flex justify-center" : "px-2",
        isActive
          ? "bg-persian-50 text-persian-990 hover:bg-persian-900 hover:text-white "
          : "",
      )}
      key={deal.id}
      onClick={() => {
        dispatch(actions.setActiveDealId({ dealId: deal.id }));
        history.push(`/deal/${deal.id}/dashboard`);
        setTimeout(() => {
          queryClient.invalidateQueries();
          invalidatePermissionChecks();
        }, 500);
      }}
    >
      {props.isCollapsed ? (
        <CompanyLogo
          size={28}
          name={deal.company.name}
          bgColor={deal.company.logoColor}
          logo={deal.company.logo}
          borderSize="0"
        />
      ) : (
        <div className="flex flex-row items-center gap-x-3">
          <CompanyLogo
            size={28}
            name={deal.company.name}
            bgColor={deal.company.logoColor}
            logo={deal.company.logo}
            borderSize="0"
          />
          <div className="flex flex-col">
            <p
              className={clsx(
                "text-sm font-semibold ",
                isActive
                  ? "text-persian-990 group-hover:text-white"
                  : "text-white",
              )}
            >
              {deal.company.name}
            </p>
            <p
              className={clsx(
                "text-xs ",
                isActive
                  ? "text-persian-950 group-hover:text-persian-50"
                  : "text-persian-50",
              )}
            >
              Last viewed{" "}
              {formatDistanceToNowStrict(fromUnixTime(lastSeen), {
                addSuffix: true,
              })}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const Nav = ({
  showMenu,
  colorScheme,
  children,
  setShowMenu,
  isCollapsed,
  isHovered,
  setIsHovered,
}: {
  showMenu: boolean;
  colorScheme: string;
  children: React.ReactNode;
  setShowMenu: (showMenu: boolean) => void;
  isCollapsed: boolean;
  isHovered: boolean;
  setIsHovered: (isHovered: boolean) => void;
}) => {
  return (
    <>
      <div
        className={clsx(
          // No overflow-hidden here — it clips account/notification popovers.
          // Deal list scroll is contained on the list itself (min-h-0 overflow-y-auto).
          "hidden md:flex from-persian-990 to-persian-950 bg-gradient-to-br h-screen sticky top-0 z-30 select-none pt-2 flex-col transition-all duration-300 ease-out",
          isCollapsed ? "min-w-[80px] w-20" : "min-w-[280px] w-70",
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {children}
      </div>
      <div className="md:hidden">
        <SlideOver
          open={showMenu}
          onClose={() => setShowMenu(false)}
          direction="left"
          backgroundColor="bg-persian-990"
        >
          <div className="w-full h-screen from-persian-990 to-persian-990/90 bg-gradient-to-br">
            {children}
          </div>
        </SlideOver>
      </div>
    </>
  );
};

// md:absolute md:left-0 md:rounded-md md:m-4 md:h-auto md:w-full md:box-border md:border-r-0 md:transition md:duration-200 md:ease-out md:shadow-md

const Link = ({
  children,
  size,
  onClick,
  mode,
  isCollapsed,
  ...props
}: {
  children: React.ReactNode;
  size?: string;
  mode?: "secondary" | "primary";
  onClick?: () => void;
  isCollapsed?: boolean;
} & WithMarginProp & {
    to: string;
    exact?: boolean;
  }) => {
  if (mode && mode === "secondary") {
    return (
      <NavLink
        {...props}
        onClick={onClick}
        key={props.to}
        className={(isActive) => {
          return classNames(
            size === "s" ? "text-sm px-0" : "text-base",
            "py-1.5 my-1.5 text-white font-semibold rounded-md flex items-center transition-all duration-300",
            isCollapsed ? "px-2 justify-center mx-2" : "px-3 gap-x-2 mx-2",
            isActive
              ? " text-persian-990"
              : "text-white hover:text-persian-300",
          );
        }}
      >
        {children}
      </NavLink>
    );
  }

  return (
    <NavLink
      {...props}
      key={props.to}
      onClick={onClick}
      className={(isActive) => {
        return classNames(
          size === "s" ? "text-sm px-0" : "text-base",
          "py-1.5 my-1.5 hover:bg-persian-900 hover:text-white font-semibold rounded-md flex items-center transition-all duration-300",
          isCollapsed ? "px-2 justify-center mx-2" : "px-3 gap-x-2 mx-2",
          isActive
            ? "bg-white text-persian-990"
            : "text-white hover:text-persian-600/90",
        );
      }}
    >
      {children}
    </NavLink>
  );
};
